import type { Locale as DateFnsLocale, LocalizeFnOptions, Month } from 'date-fns';
import { enUS } from 'date-fns/locale/en-US';
import { es } from 'date-fns/locale/es';
import { nb } from 'date-fns/locale/nb';
import i18next from 'i18next';

/**
 * Spanish short months keep the trailing dot the portal has always shown ("5 oct."), which
 * is how Spanish abbreviates; date-fns' Spanish data drops it ("5 oct").
 */
const ES_MONTHS_SHORT = [
    'ene.',
    'feb.',
    'mar.',
    'abr.',
    'may.',
    'jun.',
    'jul.',
    'ago.',
    'sep.',
    'oct.',
    'nov.',
    'dic.',
] as const;

function spanishMonth(month: Month, options?: LocalizeFnOptions): string {
    return options?.width === 'abbreviated'
        ? ES_MONTHS_SHORT[month]
        : es.localize.month(month, options);
}

const esDotted: DateFnsLocale = {
    ...es,
    localize: { ...es.localize, month: spanishMonth },
};

const DATE_LOCALES: Record<string, DateFnsLocale> = {
    en: enUS,
    nb,
    es: esDotted,
};

/**
 * The date-fns locale for an app language. A region suffix is ignored (`nb-NO` reads as
 * `nb`) and anything unknown falls back to English, as moment's locale lookup did.
 */
export function dateLocale(language: string | null | undefined): DateFnsLocale {
    const base = (language ?? '').toLowerCase().split('-')[0];
    return DATE_LOCALES[base] ?? enUS;
}

/** The language the portal is shown in: the app's i18next instance, English before it starts. */
export function appLanguage(): string {
    return i18next.resolvedLanguage ?? i18next.language ?? 'en';
}

/** The date-fns locale of the language the portal is shown in. */
export function appDateLocale(): DateFnsLocale {
    return dateLocale(appLanguage());
}
